import type {
  MembershipResource,
  WorkspaceResource,
} from '@/features/iam/api/resources/workspaces'
import type { WorkspaceDomainResource } from '@/features/iam/api/resources/domain'
import {
  type MemberMetadataFieldResource,
  type ProgressionFlowsResource,
  type WorkspaceBrandingResource,
  type WorkspaceConfigResource,
} from '@/features/iam/api/resources/workspace-config'
import type {
  MemberMetadataField,
  WorkspaceClassification,
  WorkspaceDomain,
  WorkspaceProps,
} from '@/features/iam/workspaces/Workspace'
import {
  WorkspaceConfig,
  type ProgressionFlow,
} from '@/features/iam/workspaces/workspace-config'
import { WorkspaceBranding } from '@/features/iam/workspaces/workspace-branding'
import { mapValues } from 'lodash-es'
import type { Membership } from '@/features/iam/workspaces/Membership'
import type { PrimeFullWorkspaceFn } from '@/features/iam/workspaces/WorkspaceStore'
import type { PrimeFullMemberFn } from '@/features/iam/members/MemberStore'

/**
 * Maps a membership resource, priming its workspace and member.
 */
export function mapMembershipResourceToMembership(
  resource: MembershipResource,
  primeFullWorkspace: PrimeFullWorkspaceFn,
  primeFullMember: PrimeFullMemberFn,
): Membership {
  return {
    workspace: primeFullWorkspace(
      mapWorkspaceResourceToProps(resource.workspace),
    ),
    member: primeFullMember(resource.member),
  }
}

export function mapWorkspaceResourceToProps(
  resource: WorkspaceResource,
): Partial<WorkspaceProps> {
  return {
    id: resource.id,
    name: resource.name,
    description: resource.description,
    slug: resource.slug,
    domains: resource.domains?.map(mapWorkspaceDomainResourceToProps),
    config: resource.config
      ? mapWorkspaceConfigResourceToProps(resource.config)
      : undefined,
    branding: resource.branding
      ? mapWorkspaceBrandingResourceToProps(resource.branding)
      : undefined,
    memberMetadataFields: resource.custom_metadata_fields
      ? (resource.custom_metadata_fields.member_metadata_fields ?? []).map(
          mapMemberMetadataFieldResourceToProps,
        )
      : undefined,
    archivalStatus: resource.archival_status,
    classification: resource.classification
      ? mapWorkspaceClassificationResourceToClassification(
          resource.classification,
        )
      : undefined,
  }
}

export function mapWorkspaceConfigResourceToProps(
  resource: WorkspaceConfigResource,
): WorkspaceConfig {
  const billingEnabled = resource.billing?.enabled || false
  const contract = resource.contract_config
  return {
    billing: {
      enabled: billingEnabled,
      couponsEnabled:
        (billingEnabled && resource.billing?.['coupons.enabled']) || false,
      stripeConnectId: resource.billing?.stripe_connect_id,
    },
    termsOfService: {
      showOnLogin: resource.tos?.show_on_login || false,
      hideOnWortal: resource.tos?.hide_on_wortal || false,
      customTermsEnabled:
        resource.tos?.custom_source !== undefined &&
        resource.tos?.custom_source !== null,
    },
    signup: {
      logoutUrl: resource.signup?.logout_url,
      federatedLogout: Boolean(resource.signup?.federated_logout),
      externalProfiles: Boolean(resource.signup?.external_profiles),
    },
    jobs: {
      anonymousOpportunities:
        resource.job_config?.anonymous_opportunities || false,
      progressionFlow: resource.job_config?.job_progression_flow || false,
    },
    jobProgression: {
      enabled: resource.job_progression_config?.enabled || false,
      legends: mapValues(
        resource.job_progression_config?.legends ?? {},
        mapProgressionFlowResourceToProgressionFlow,
      ),
    },
    notifications: {
      allowSnoozing: resource.notifications_config?.allow_snoozing ?? false,
    },
    messageBank: {
      enabled: resource.message_bank_config?.enabled ?? false,
    },
    combinedPricing: {
      jobScopeChangeRequestsEnabled: Boolean(
        resource.combined_pricing?.job_scope_change_requests_enabled,
      ),
      jobScopeRequireClientApproval: Boolean(
        resource.combined_pricing?.job_scope_require_client_approval,
      ),
    },
    payouts: {
      enabled: Boolean(resource.payouts?.enabled),
    },
    sureprep: {
      enabled: Boolean(resource.sureprep?.enabled),
    },
    insights: {
      enabled: resource.insights_config?.enabled ?? false,
    },
    contract: contract
      ? {
          enabled: Boolean(contract.enabled),
          amount: contract.amount ?? 0,
          vmFees: {
            amountPerUnit: contract.vm_fees?.amount_per_unit ?? 0,
            quantity: contract.vm_fees?.quantity ?? 0,
          },
          setupFeeAmount: contract.setup_fees?.amount ?? 0,
          startDate: new Date(contract.start_date),
          endDate: new Date(contract.end_date),
        }
      : WorkspaceConfig.default.contract,
    staffing: {
      enabled: Boolean(resource.staffing_config?.enabled),
    },
    legendsBuilder: {
      enabled: Boolean(resource.legends_builder_config?.enabled),
    },
    externalCustomer: {
      enabled: Boolean(resource.external_customers_config?.enabled),
      allowRequestingMagicLinks: Boolean(
        resource.external_customers_config?.allow_requesting_magic_links,
      ),
      clientAssignmentEnabled: Boolean(
        resource.external_customers_config?.client_assignment_enabled,
      ),
    },
    callCenter: {
      enabled: Boolean(resource.call_center_config?.enabled),
      totalRequiredQuestions:
        resource.call_center_config?.total_required_questions ?? 0,
    },
    customMenu:
      resource.custom_menu?.items.map((item) => ({
        label: item.label,
        target: item.target,
        audience: item.audience,
        url: item.url,
      })) ?? [],
  }
}

/**
 * Parts missing from the resource fall back to the default branding.
 */
function mapWorkspaceBrandingResourceToProps(
  resource: Partial<WorkspaceBrandingResource>,
): WorkspaceBranding {
  const fallback = WorkspaceBranding.default
  return {
    corporateMode: resource.corporate
      ? resource.corporate.enabled
      : fallback.corporateMode,
    logos: resource.logos
      ? {
          primary: resource.logos.primary ?? '',
          primaryWhite: resource.logos.primary_white ?? '',
          icon: resource.logos.icon ?? '',
          iconWhite: resource.logos.icon_white ?? '',
        }
      : fallback.logos,
    colors: resource.colors
      ? { primary: resource.colors.primary }
      : fallback.colors,
    landingPage: resource.landing_page
      ? { image: resource.landing_page.image }
      : fallback.landingPage,
  }
}

function mapWorkspaceDomainResourceToProps(
  resource: WorkspaceDomainResource,
): WorkspaceDomain {
  return {
    type: resource.domain_type,
    domain: resource.domain,
  }
}

function mapMemberMetadataFieldResourceToProps(
  resource: MemberMetadataFieldResource,
): MemberMetadataField {
  return {
    id: resource.id,
    type: resource.type,
    title: resource.title,
    description: resource.description,
    active: resource.active,
    permissions: resource.permissions,
  }
}

function mapProgressionFlowResourceToProgressionFlow(
  flow: ProgressionFlowsResource,
): ProgressionFlow {
  // The workspace config stores legends unvalidated, so an unexpected value
  // must not stop the whole workspace from loading.
  switch (flow) {
    case 'OUTSOURCING_PROGRESSION':
      return 'OutsourcingProgression'
    case 'RETAIL_PROGRESSION':
      return 'RetailProgression'
    case 'NONE':
      return 'None'
    default:
      console.warn('unknown progression flow', flow)
      return 'Unknown'
  }
}

function mapWorkspaceClassificationResourceToClassification(
  classification: NonNullable<WorkspaceResource['classification']>,
): WorkspaceClassification {
  switch (classification) {
    case 'B2C':
      return 'B2C'
    case 'B2B':
      return 'B2B'
    case 'B2B2C':
      return 'B2B2C'
    case 'B2E':
      return 'B2E'
    case 'DEMO':
      return 'DEMO'
    case 'FIRM':
      return 'FIRM'
    case 'TXF':
      return 'TXF'
  }
}
